/* eslint-disable react/no-unused-prop-types */
import {
    ReactNode, createContext, useCallback, useContext, useMemo,
} from 'react';
import { Column, Option, RenderIf } from 'react-rainbow-components';
import { useReduxForm } from '@rainbow-modules/hooks';
import { PicklistValue } from '@rainbow-modules/listview/lib/components/AuditLogs/types';
import Button from 'components/Button';
import ButtonIcon from 'components/ButtonIcon';
import Plus from 'components/icons/plus';
import Trash from 'components/icons/trash';
import {
    InvoiceBuilderColumn,
    InvoiceBuilderCustomColumn,
} from '../types';
import {
    customColumns,
    dollarsToMinor,
    formatMinor,
    fromIsoDate,
    lineDraftFromColumns,
    lineTotalMinor,
    newIdempotencyKey,
    toIsoDate,
} from '../helpers';
import {
    AddLineButton,
    LineActionsCell,
    LineCellWrapper,
    LineItemsContainer,
    LineItemsError,
    StyledDatePicker,
    StyledInput,
    StyledLineTable,
    StyledPicklist,
} from './styled';

export interface LineDraft {
    key: string;
    description: string;
    quantity: string;
    rate: string;
    customValues: Record<string, string>;
}

export const emptyLine = (columns: InvoiceBuilderColumn[] = []): LineDraft => ({
    key: newIdempotencyKey(),
    ...lineDraftFromColumns(columns),
});

export const isInvalidLineInput = (
    line: LineDraft,
    column: InvoiceBuilderColumn,
) => {
    if (column.kind === 'description') return !line.description?.trim();
    if (column.kind === 'quantity') return !(Number(line.quantity) > 0);
    if (column.kind === 'custom' && column.isRequired) {
        return !line.customValues?.[column.id]?.trim();
    }
    return false;
};

export const isInvalidLine = (
    line: LineDraft,
    columns: InvoiceBuilderColumn[] = [],
) => (
    !line.description?.trim()
    || !(Number(line.quantity) > 0)
    || customColumns(columns).some((column) => isInvalidLineInput(line, column))
);

interface LineItemsContextValue {
    error?: ReactNode;
    columns: InvoiceBuilderColumn[];
    currency: string;
    locale: string;
    canRemove: boolean;
    onUpdateLine: (key: string, patch: Partial<LineDraft>) => void;
    onUpdateCustom: (key: string, columnId: string, value: string) => void;
    onRemove: (key: string) => void;
}

const LineItemsContext = createContext<LineItemsContextValue>({
    columns: [],
    currency: 'USD',
    locale: 'en-US',
    canRemove: false,
    onUpdateLine: () => {},
    onUpdateCustom: () => {},
    onRemove: () => {},
});

interface LineCellProps {
    row?: LineDraft;
    column?: InvoiceBuilderColumn;
    cellAlignment?: string;
}

const LineCell = ({ row, column, cellAlignment }: LineCellProps) => {
    const {
        error,
        currency,
        locale,
        onUpdateLine,
        onUpdateCustom,
    } = useContext(LineItemsContext);

    if (!row || !column) return null;

    const invalid = Boolean(error) && isInvalidLineInput(row, column);

    if (column.kind === 'description') {
        return (
            <LineCellWrapper $alignment={cellAlignment}>
                <StyledInput
                    hideLabel
                    label={column.label}
                    placeholder={column.label}
                    borderRadius="semi-square"
                    value={row.description}
                    error={invalid ? true : undefined}
                    onChange={(event) => onUpdateLine(row.key, {
                        description: event.target.value,
                    })}
                />
            </LineCellWrapper>
        );
    }
    if (column.kind === 'quantity') {
        return (
            <LineCellWrapper $alignment={cellAlignment}>
                <StyledInput
                    hideLabel
                    label={column.label}
                    type="number"
                    min="0"
                    step="0.01"
                    borderRadius="semi-square"
                    value={row.quantity}
                    error={invalid ? true : undefined}
                    onChange={(event) => onUpdateLine(row.key, {
                        quantity: event.target.value,
                    })}
                />
            </LineCellWrapper>
        );
    }
    if (column.kind === 'rate') {
        return (
            <LineCellWrapper $alignment={cellAlignment}>
                <StyledInput
                    hideLabel
                    label={column.label}
                    type="number"
                    min="0"
                    step="0.01"
                    borderRadius="semi-square"
                    value={row.rate}
                    onChange={(event) => onUpdateLine(row.key, {
                        rate: event.target.value,
                    })}
                />
            </LineCellWrapper>
        );
    }
    if (column.kind === 'amount') {
        return (
            <LineCellWrapper $alignment={cellAlignment}>
                {formatMinor(
                    lineTotalMinor(row.quantity, dollarsToMinor(row.rate)),
                    currency,
                    locale,
                )}
            </LineCellWrapper>
        );
    }

    const customColumn = column as InvoiceBuilderCustomColumn;
    const value = row.customValues[customColumn.id] || '';
    if (customColumn.valueKind === 'boolean') {
        return (
            <LineCellWrapper $alignment={cellAlignment}>
                <StyledPicklist
                    hideLabel
                    label={customColumn.label}
                    placeholder="Select"
                    error={invalid ? true : undefined}
                    value={
                        value
                            ? {
                                name: value,
                                label: value === 'true' ? 'Yes' : 'No',
                            }
                            : undefined
                    }
                    onChange={(next: PicklistValue) => onUpdateCustom(
                        row.key,
                        customColumn.id,
                        String(next?.name || ''),
                    )}
                >
                    <Option name="true" label="Yes" />
                    <Option name="false" label="No" />
                </StyledPicklist>
            </LineCellWrapper>
        );
    }
    if (customColumn.valueKind === 'date') {
        return (
            <LineCellWrapper $alignment={cellAlignment}>
                <StyledDatePicker
                    hideLabel
                    label={customColumn.label}
                    value={fromIsoDate(value)}
                    error={invalid ? true : undefined}
                    onChange={(date: Date) => onUpdateCustom(
                        row.key,
                        customColumn.id,
                        toIsoDate(date),
                    )}
                />
            </LineCellWrapper>
        );
    }
    return (
        <LineCellWrapper $alignment={cellAlignment}>
            <StyledInput
                hideLabel
                label={customColumn.label}
                type={customColumn.valueKind === 'number' ? 'number' : 'text'}
                borderRadius="semi-square"
                value={value}
                error={invalid ? true : undefined}
                onChange={(event) => onUpdateCustom(
                    row.key,
                    customColumn.id,
                    event.target.value,
                )}
            />
        </LineCellWrapper>
    );
};

const LineActions = ({ row }: { row?: LineDraft }) => {
    const { canRemove, onRemove } = useContext(LineItemsContext);
    if (!row) return null;
    return (
        <LineActionsCell>
            <ButtonIcon
                type="button"
                size="small"
                borderRadius="semi-square"
                title="Remove line"
                aria-label="Remove line"
                icon={<Trash />}
                disabled={!canRemove}
                onClick={() => onRemove(row.key)}
            />
        </LineActionsCell>
    );
};

const columnField = (column: InvoiceBuilderColumn) => (
    column.kind === 'custom' ? 'customValues' : column.kind
);

const columnWidth = (column: InvoiceBuilderColumn) => {
    if (column.kind === 'quantity') return 150;
    if (column.kind === 'rate') return 150;
    if (column.kind === 'amount') return 150;
    return undefined;
};

const columnAlignment = (column: InvoiceBuilderColumn) => {
    if (column.kind === 'amount') return 'right';
    return undefined;
};

interface LineItemsInputProps {
    id?: string;
    className?: string;
    name?: string;
    value?: LineDraft[];
    onChange?: (value: LineDraft[]) => void;
    error?: ReactNode;
    columns?: InvoiceBuilderColumn[];
    currency?: string;
    locale?: string;
}

const LineItemsInput = (props: LineItemsInputProps) => {
    const {
        id,
        className,
        value: valueInProps,
        onChange = () => {},
        error,
        columns = [],
        currency = 'USD',
        locale = 'en-US',
    } = useReduxForm(props);

    const lines = useMemo(() => valueInProps || [], [valueInProps]);

    const onUpdateLine = useCallback((key: string, patch: Partial<LineDraft>) => {
        onChange(lines.map((line) => (
            line.key === key ? { ...line, ...patch } : line
        )));
    }, [lines, onChange]);

    const onUpdateCustom = useCallback((key: string, columnId: string, nextValue: string) => {
        onChange(lines.map((line) => (
            line.key === key
                ? {
                    ...line,
                    customValues: {
                        ...line.customValues,
                        [columnId]: nextValue,
                    },
                }
                : line
        )));
    }, [lines, onChange]);

    const onRemove = useCallback((key: string) => {
        onChange(lines.filter((line) => line.key !== key));
    }, [lines, onChange]);

    const contextValue = useMemo(() => ({
        error,
        columns,
        currency,
        locale,
        canRemove: lines.length > 1,
        onUpdateLine,
        onUpdateCustom,
        onRemove,
    }), [
        error,
        columns,
        currency,
        locale,
        lines.length,
        onUpdateLine,
        onUpdateCustom,
        onRemove,
    ]);

    return (
        <LineItemsContext.Provider value={contextValue}>
            <LineItemsContainer id={id} className={className}>
                <StyledLineTable
                    keyField="key"
                    data={lines}
                    resizeColumnDisabled
                >
                    {columns.map((column) => (
                        <Column
                            key={column.id}
                            header={column.label}
                            field={columnField(column)}
                            component={LineCell}
                            column={column}
                            cellAlignment={columnAlignment(column)}
                            headerAlignment={columnAlignment(column)}
                            defaultWidth={columnWidth(column)}
                        />
                    ))}
                    <Column
                        header=""
                        field="key"
                        defaultWidth={40}
                        component={LineActions}
                    />
                </StyledLineTable>
                <RenderIf isTrue={Boolean(error)}>
                    <LineItemsError>{error}</LineItemsError>
                </RenderIf>
                <AddLineButton>
                    <Button
                        type="button"
                        variant="base"
                        size="small"
                        borderRadius="semi-square"
                        onClick={() => onChange([...lines, emptyLine(columns)])}
                    >
                        <Plus className="rainbow-m-right_small" />
                        Add line
                    </Button>
                </AddLineButton>
            </LineItemsContainer>
        </LineItemsContext.Provider>
    );
};

export default LineItemsInput;
